import { CANONICAL_ROLES, FIELD_TYPES, type FieldDefinition, type FieldRules, type FormSchema } from './types.ts';
import { MAX_PHONE_REGIONS, PHONE_REGIONS } from './phone-regions.ts';
import { RULES_BY_TYPE, isControlField } from './validate.ts';

export interface SchemaIssue {
  /** Field key, or `*` for a problem with the schema as a whole. */
  field: string;
  message: string;
}

const KEY_PATTERN = /^[A-Za-z][A-Za-z0-9_-]{0,63}$/u;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/u;
const MAX_PATTERN_LENGTH = 200;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function checkRules(field: FieldDefinition, issues: SchemaIssue[]): void {
  const rules: FieldRules = field.rules ?? {};
  const allowed = RULES_BY_TYPE[field.type];
  const add = (message: string): void => {
    issues.push({ field: field.key, message });
  };

  for (const name of Object.keys(rules)) {
    // cast: Object.keys() returns string[]; the rule names are checked against the allowed list right here.
    if (!allowed.includes(name as keyof FieldRules)) add(`rule "${name}" does not apply to type "${field.type}"`);
  }
  for (const name of ['minLength', 'maxLength', 'maxItems'] as const) {
    if (rules[name] !== undefined && !isNonNegativeInteger(rules[name])) add(`rule "${name}" must be a non-negative integer`);
  }
  if (rules.minLength !== undefined && rules.maxLength !== undefined && rules.minLength > rules.maxLength) {
    add('minLength is greater than maxLength');
  }
  for (const name of ['min', 'max'] as const) {
    if (rules[name] !== undefined && !Number.isFinite(rules[name])) add(`rule "${name}" must be a finite number`);
  }
  if (rules.min !== undefined && rules.max !== undefined && rules.min > rules.max) add('min is greater than max');
  if (rules.pattern !== undefined) {
    if (rules.pattern.length > MAX_PATTERN_LENGTH) add(`pattern is longer than ${MAX_PATTERN_LENGTH} characters`);
    try {
      new RegExp(`^(?:${rules.pattern})$`, 'u');
    } catch {
      add('pattern is not a valid regular expression');
    }
  }
  if (field.type === 'select' || field.type === 'multiselect') {
    if (!rules.options || rules.options.length === 0) add(`type "${field.type}" needs a non-empty "options" list`);
    else if (new Set(rules.options).size !== rules.options.length) add('options contain duplicates');
  }
  for (const name of ['minDate', 'maxDate'] as const) {
    const value = rules[name];
    if (value !== undefined && !ISO_DATE.test(value)) add(`rule "${name}" must be an ISO date (YYYY-MM-DD)`);
  }
  if (rules.minDate !== undefined && rules.maxDate !== undefined && rules.minDate > rules.maxDate) add('minDate is after maxDate');
  checkPhoneRegions(rules, add);
}

/**
 * `phoneRegions` (or the older single `phoneRegion`): known country codes only, since a code
 * libphonenumber does not know would quietly turn every national number away (spec §19 #72).
 */
function checkPhoneRegions(rules: FieldRules, add: (message: string) => void): void {
  // Read as unknown: the rules object has not been shape-checked yet.
  const list: unknown = rules.phoneRegions;
  const single: unknown = rules.phoneRegion;
  if (list !== undefined && single !== undefined) add('use either phoneRegions or phoneRegion, not both');
  const items: unknown[] = Array.isArray(list) ? list : [];
  if (list !== undefined && !Array.isArray(list)) add('phoneRegions must be a list of country codes');
  if (items.length > MAX_PHONE_REGIONS) add(`phoneRegions names more than ${MAX_PHONE_REGIONS} countries`);
  if (new Set(items).size !== items.length) add('phoneRegions contains duplicates');
  const codes = single === undefined ? items : [...items, single];
  for (const code of codes) {
    if (typeof code !== 'string' || !PHONE_REGIONS.includes(code)) add(`${JSON.stringify(code)} is not a country code phone numbers can be read for (ISO 3166-1, two capital letters, e.g. "US")`);
  }
}

/**
 * Validates a form schema itself (FR-5.4, FR-5.5). Takes `unknown` on purpose: schemas arrive from
 * YAML files and API requests, and this function is the gate that makes the `FormSchema` type true.
 * Returns the list of problems; an empty list means the value is a valid `FormSchema`.
 */
export function validateFormSchema(schema: unknown): SchemaIssue[] {
  const issues: SchemaIssue[] = [];
  if (!isRecord(schema) || !Array.isArray(schema['fields'])) return [{ field: '*', message: 'schema must be an object with a "fields" array' }];
  const fields: unknown[] = schema['fields'];
  if (fields.length === 0) issues.push({ field: '*', message: 'schema has no fields' });

  const seenKeys = new Set<string>();
  const seenRoles = new Set<string>();

  fields.forEach((raw, index) => {
    if (!isRecord(raw)) {
      issues.push({ field: `#${index}`, message: 'field must be an object' });
      return;
    }
    const key = raw['key'];
    if (typeof key !== 'string' || !KEY_PATTERN.test(key) || isControlField(key)) {
      issues.push({ field: `#${index}`, message: 'key must start with a letter and contain only letters, digits, "_" or "-" (max 64)' });
      return;
    }
    if (seenKeys.has(key)) issues.push({ field: key, message: 'duplicate field key' });
    seenKeys.add(key);

    if (typeof raw['label'] !== 'string' || raw['label'].trim() === '') issues.push({ field: key, message: 'label is required' });
    // cast: FIELD_TYPES is a readonly tuple of literals; includes() needs the wider element type to test an unknown.
    if (typeof raw['type'] !== 'string' || !(FIELD_TYPES as readonly string[]).includes(raw['type'])) {
      issues.push({ field: key, message: `type must be one of: ${FIELD_TYPES.join(', ')}` });
      return;
    }
    for (const flag of ['required', 'pii'] as const) {
      if (raw[flag] !== undefined && typeof raw[flag] !== 'boolean') issues.push({ field: key, message: `"${flag}" must be a boolean` });
    }
    const role = raw['role'];
    if (role !== undefined) {
      // cast: same widening as FIELD_TYPES above.
      if (typeof role !== 'string' || !(CANONICAL_ROLES as readonly string[]).includes(role)) {
        issues.push({ field: key, message: `role must be one of: ${CANONICAL_ROLES.join(', ')}` });
      } else {
        if (seenRoles.has(role)) issues.push({ field: key, message: `role "${role}" is already used by another field` });
        seenRoles.add(role);
      }
    }
    if (raw['rules'] !== undefined && !isRecord(raw['rules'])) {
      issues.push({ field: key, message: '"rules" must be an object' });
      return;
    }
    // cast: every property read by checkRules is validated there; shape checks above cover the rest.
    checkRules(raw as unknown as FieldDefinition, issues);
  });

  return issues;
}

/** Narrowing helper built on {@link validateFormSchema}. */
export function isFormSchema(schema: unknown): schema is FormSchema {
  return validateFormSchema(schema).length === 0;
}
